import { useState } from "react";
import { Plus, X, ChevronDown, TrendingDown, Zap, Wrench, Package } from "lucide-react";
import { useGetTransazioni, useGetStrutture, useCreateTransazione, useDeleteTransazione } from "../lib/supabaseQueries";

type Tab = "variabili" | "fissi" | "utenze";
const TAB_CONFIG: Record<Tab, { label: string; icon: any; categoria: string; colorClass: string }> = {
  variabili: { label: "Costi Variabili", icon: Package, categoria: "Variabile", colorClass: "text-amber-600" },
  fissi:     { label: "Costi Fissi",     icon: Wrench,  categoria: "Fisso",     colorClass: "text-indigo-600" },
  utenze:    { label: "Utenze",          icon: Zap,     categoria: "Utenza",    colorClass: "text-emerald-600" },
};
const fmt = (n: number) => "€" + n.toLocaleString("it-IT");
const today = new Date().toISOString().slice(0, 10);

type FormState = { data: string; data_fine: string; descrizione: string; struttura_nome: string; importo: number; note: string; periodo: string };

function TabContent({ tab }: { tab: Tab }) {
  const cfg = TAB_CONFIG[tab];
  const { data: transazioni = [], isLoading } = useGetTransazioni();
  const { data: strutture = [] } = useGetStrutture();
  const createTx = useCreateTransazione();
  const deleteTx = useDeleteTransazione();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>({ data: today, data_fine: "", descrizione: "", struttura_nome: strutture[0]?.nome ?? "", importo: 0, note: "", periodo: "" });
  const [filStrutt, setFilStrutt] = useState("");

  const voci = transazioni.filter((t: any) => t.tipo === "Uscita" && t.categoria === cfg.categoria);
  const filtered = filStrutt ? voci.filter((v: any) => v.struttura_nome === filStrutt) : voci;
  const totale = filtered.reduce((a: number, v: any) => a + (Number(v.importo) || 0), 0);

  const struttureOpts = strutture.map((s: any) => s.nome);

  const perStruttura = strutture.map((s: any) => ({
    nome: s.nome,
    tot: voci.filter((v: any) => v.struttura_nome === s.nome).reduce((a: number, v: any) => a + (Number(v.importo) || 0), 0),
  }));
  const maxTot = Math.max(...perStruttura.map(s => s.tot), 1);

  const save = async () => {
    if (!form.descrizione || !form.importo) return;
    const payload: any = { tipo: "Uscita", categoria: cfg.categoria, descrizione: form.descrizione, data: form.data, struttura_nome: form.struttura_nome, importo: form.importo, note: form.note };
    if (form.periodo) payload.periodo = form.periodo;
    if (form.data_fine) payload.data_fine = form.data_fine;
    await createTx.mutateAsync(payload);
    setForm({ data: today, data_fine: "", descrizione: "", struttura_nome: struttureOpts[0] ?? "", importo: 0, note: "", periodo: "" });
    setShowForm(false);
  };

  if (isLoading) return <div className="py-10 text-center text-muted-foreground text-sm">Caricamento...</div>;

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-1">
            <cfg.icon className={`h-4 w-4 ${cfg.colorClass}`} />
            <span className="text-xs text-muted-foreground">Totale {cfg.label}</span>
          </div>
          <div className="text-xl font-bold text-foreground">{fmt(voci.reduce((a: number, v: any) => a + (Number(v.importo) || 0), 0))}</div>
          <div className="text-xs text-muted-foreground">{voci.length} voci</div>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4 col-span-2">
          <p className="text-xs font-medium text-muted-foreground mb-2">Per struttura</p>
          {perStruttura.length === 0
            ? <p className="text-xs text-muted-foreground">Nessuna struttura</p>
            : <div className="space-y-1.5">
                {perStruttura.map(s => (
                  <div key={s.nome} className="flex items-center gap-2">
                    <span className="text-xs text-foreground w-28 truncate">{s.nome}</span>
                    <div className="flex-1 bg-muted/50 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${cfg.colorClass.replace("text-","bg-")}`} style={{ width: `${(s.tot / maxTot) * 100}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-foreground w-16 text-right">{fmt(s.tot)}</span>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="relative">
          <select value={filStrutt} onChange={e => setFilStrutt(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl border border-border bg-card text-sm text-foreground focus:outline-none">
            <option value="">Tutte le strutture</option>
            {struttureOpts.map(s => <option key={s}>{s}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-foreground text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 transition-opacity">
          <Plus className="h-4 w-4" /> Aggiungi voce
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground text-sm">Nuova voce — {cfg.label}</h3>
            <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-muted/50"><X className="h-4 w-4 text-muted-foreground" /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Data inizio</label>
              <input type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Data fine (opz.)</label>
              <input type="date" value={form.data_fine} onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Periodo fatturazione</label>
              <input type="text" value={form.periodo} onChange={e => setForm(f => ({ ...f, periodo: e.target.value }))}
                placeholder="es. Gen 2025, Q1 2025…"
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Descrizione</label>
              <input type="text" value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
                placeholder="es. Pulizie, bolletta..."
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Struttura</label>
              <div className="relative">
                <select value={form.struttura_nome} onChange={e => setForm(f => ({ ...f, struttura_nome: e.target.value }))}
                  className="w-full appearance-none pl-3 pr-8 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary">
                  <option value="">— Seleziona —</option>
                  {struttureOpts.map(s => <option key={s}>{s}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Importo (€)</label>
              <input type="number" value={form.importo || ""} onChange={e => setForm(f => ({ ...f, importo: +e.target.value }))}
                min={0} placeholder="0"
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Note (opzionale)</label>
              <input type="text" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                placeholder="Note aggiuntive..."
                className="w-full px-3 py-2 rounded-xl border border-border text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-border text-sm text-muted-foreground">Annulla</button>
            <button onClick={save} disabled={createTx.isPending}
              className="px-4 py-2 rounded-xl bg-foreground text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {createTx.isPending ? "Salvataggio..." : "Aggiungi"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-10 text-center">
          <TrendingDown className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">Nessuna voce registrata</p>
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  {["Data", "Periodo", "Descrizione", "Struttura", "Note", "Importo", ""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((v: any) => (
                  <tr key={v.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{v.data || "—"}{v.data_fine ? ` → ${v.data_fine}` : ""}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{v.periodo || "—"}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{v.descrizione}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{v.struttura_nome || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{v.note || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-foreground">{fmt(Number(v.importo) || 0)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => deleteTx.mutate(v.id)}
                        className="text-xs px-2 py-1 rounded-lg border border-border text-red-400 hover:border-red-300">Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/40 border-t border-border">
                  <td colSpan={5} className="px-4 py-3 text-xs font-bold text-muted-foreground">TOTALE ({filtered.length} voci)</td>
                  <td className="px-4 py-3 text-sm font-bold text-foreground">{fmt(totale)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FinanzeUscite() {
  const [tab, setTab] = useState<Tab>("variabili");
  const { data: transazioni = [] } = useGetTransazioni();

  const totali = Object.fromEntries(
    (Object.keys(TAB_CONFIG) as Tab[]).map(k => [
      k,
      transazioni.filter((t: any) => t.tipo === "Uscita" && t.categoria === TAB_CONFIG[k].categoria)
                 .reduce((a: number, t: any) => a + (Number(t.importo) || 0), 0),
    ])
  );
  const totaleGlobale = Object.values(totali).reduce((a, v) => a + v, 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-foreground">Uscite</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Costi variabili, fissi e utenze — Totale: <span className="font-semibold text-foreground">{fmt(totaleGlobale)}</span></p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit mb-6">
        {(Object.keys(TAB_CONFIG) as Tab[]).map(k => {
          const cfg = TAB_CONFIG[k];
          return (
            <button key={k} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              <cfg.icon className={`h-3.5 w-3.5 ${tab === k ? cfg.colorClass : ""}`} />
              {cfg.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === k ? "bg-muted/50 text-foreground" : "bg-card text-muted-foreground"}`}>
                {fmt(totali[k])}
              </span>
            </button>
          );
        })}
      </div>

      <TabContent key={tab} tab={tab} />
    </div>
  );
}
